import { useFreeTrialEligibilityRulesQuery } from './FreeTrialEligibilityRulesQuery.generated';
import { useAddFreeTrialCreditMutation } from 'app/src/components/UpgradePrompts/AddFreeTrialCreditMutation.generated';
import { TeamBillingStatementsDocument } from 'app/src/components/BillingDetails/BillingHistory/TeamBillingStatementsQuery.generated';
import type { TrialSource } from '@trello/organizations';
import { getFreeTrialProperties } from '@trello/organizations';
import { MoonshotOrganizationDocument } from 'app/src/components/Moonshot/MoonshotOrganizationQuery.generated';
import { needsCC } from '@trello/paid-account';
import { navigate } from 'app/scripts/controller/navigate';
import { Urls } from 'app/scripts/controller/urls';
import type { SourceType } from '@trello/atlassian-analytics';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { getNetworkError } from '@trello/graphql-error-handling';
import { Alerts } from 'app/scripts/views/lib/Alerts';
import { idToDate } from '@trello/dates';
import moment from 'moment';
import { isCompletelyFreeTrial } from 'app/src/components/Moonshot/experimentVariation';
import { useStandardPremiumFreeTrial } from 'app/src/components/BillingDetails/useStandardPremiumFreeTrial';

export interface StartFreeTrialOptions {
  redirect?: boolean;
  count?: number;
}
export interface FreeTrialRules {
  isAdmin: boolean;
  hasCredit: boolean;
  hasActivePromoCredits: boolean;
  isTrialActive?: boolean;
  isExpired?: boolean;
  isEligible?: boolean;
  isStandard: boolean;
  isPremium: boolean;
  isEnterpriseMember: boolean;
  loading: boolean;
  daysLeft: number;
  trialSource?: TrialSource;
  startDate?: Date;
  days?: number;
  startFreeTrial: (
    options?: StartFreeTrialOptions,
    source?: SourceType,
  ) => Promise<void>;
  addingTrial: boolean;
  endDate: Date | null;
  totalFreeTrialCredits: number;
}

export const useFreeTrialEligibilityRules = (
  orgId?: string,
  options: { skip: boolean } = { skip: false },
): FreeTrialRules => {
  const { data, loading } = useFreeTrialEligibilityRulesQuery({
    variables: {
      orgId: orgId || '',
    },
    skip: !orgId || options.skip,
    waitOn: ['MemberHeader', 'MemberBoards'],
  });
  const [
    addFreeTrialCredit,
    { loading: addingTrial },
  ] = useAddFreeTrialCreditMutation();

  const member = data?.member;
  const organization = data?.organization;
  const memberId = member?.id || '';
  const products = organization?.products || [];
  const isStandard = Boolean(
    organization?.premiumFeatures?.includes('isStandard'),
  );
  const isPremium = Boolean(
    organization?.premiumFeatures?.includes('isPremium'),
  );
  const isEnterpriseMember = !!member?.enterprises?.find(
    (ent) => ent.id === member?.idEnterprise,
  )?.isRealEnterprise;
  const isConfirmed = member?.confirmed;
  const orgName = organization?.name;
  const credits = organization?.credits || [];
  const isDisabled = needsCC(organization?.paidAccount?.standing);
  // The date we last reset free trials
  const OLD_FREE_TRIAL_RESET_DATE = new Date('2021-02-09T00:00:00+0000');
  // The most recent GTM date we reset Free Trials.
  // Ensure parity with server's addFreeTrial method
  const FREE_TRIAL_RESET_DATE = new Date('2022-02-22T00:00:00+0000');

  // Dynamically change Free Trial Reset date to ensure
  // Seamless swap between old and new dates
  const gtmDate =
    new Date() > FREE_TRIAL_RESET_DATE
      ? FREE_TRIAL_RESET_DATE
      : OLD_FREE_TRIAL_RESET_DATE;

  const {
    freeTrialEndDate,
    isStandardPremiumFreeTrialActive,
  } = useStandardPremiumFreeTrial(orgId || '');
  const trialProperties = getFreeTrialProperties(
    credits,
    products,
    isStandardPremiumFreeTrialActive
      ? freeTrialEndDate
      : data?.organization?.paidAccount?.trialExpiration,
  );
  const hasCredit = !!trialProperties?.credit;
  const hasNewCredit = credits.some(
    (credit) =>
      credit.type === 'freeTrial' &&
      // Ignore free trials that expired before our most recent
      // GTM Date (2/22/22), when we last reset Free Trials for users
      moment(idToDate(credit.id)).add(credit.count, 'day').isAfter(gtmDate),
  );

  const hasActivePromoCredits = credits.some(
    (credit) =>
      credit.type === 'promoCode' &&
      Boolean(credit.count) &&
      moment(idToDate(credit.id)).add(credit.count, 'day').isAfter(gtmDate),
  );
  const hasStandardPromoCredits = isStandard && hasActivePromoCredits;
  const totalFreeTrialCredits = credits.filter(
    (credit) => credit.type === 'freeTrial',
  ).length;
  const isAdmin = !!organization?.memberships.find(
    (member) => member.idMember === memberId && member.memberType === 'admin',
  );

  const startFreeTrial = async (
    { redirect, count }: StartFreeTrialOptions = { redirect: true },
    source?: SourceType,
  ) => {
    if (!orgId || loading || hasNewCredit || addingTrial) {
      return;
    }

    const trialSource = source ? source : getScreenFromUrl();

    const traceId = Analytics.startTask({
      taskName: 'edit-organization/freeTrial',
      source: trialSource,
    });

    try {
      await addFreeTrialCredit({
        variables: {
          orgId,
          ...(count ? { count } : {}),
          traceId,
        },
        refetchQueries: [
          {
            query: TeamBillingStatementsDocument,
            variables: {
              orgId,
            },
            context: {
              operationName: 'TeamBillingStatements',
              document: TeamBillingStatementsDocument,
            },
          },
          ...(isCompletelyFreeTrial()
            ? [
                {
                  query: MoonshotOrganizationDocument,
                  variables: {
                    orgId,
                  },
                  context: {
                    operationName: 'MoonshotOrganization',
                    document: MoonshotOrganizationDocument,
                  },
                },
              ]
            : []),
        ],
      });

      Analytics.taskSucceeded({
        taskName: 'edit-organization/freeTrial',
        traceId,
        source: trialSource,
      });

      if (source) {
        Analytics.sendTrackEvent({
          action: 'accepted',
          actionSubject: 'freeTrial',
          source,
          containers: {
            organization: {
              id: orgId,
            },
          },
        });

        // Update default analytics context
        Analytics.mergeContext({
          organization: {
            paidStatus: 'bc',
          },
          workspace: {
            paidStatus: 'bc',
          },
        });
      }
    } catch (e) {
      Alerts.showLiteralText(e, 'error', 'useFreeTrialEligibilityRules', 5000);
      // @ts-expect-error
      const networkError = getNetworkError(e);

      Analytics.taskFailed({
        // @ts-expect-error
        error: e,
        taskName: 'edit-organization/freeTrial',
        traceId,
        source: trialSource,
      });

      if (source) {
        Analytics.sendOperationalEvent({
          action: 'errored',
          actionSubject: 'freeTrial',
          source,
          attributes: {
            // @ts-expect-error
            errorMessage: networkError?.message ?? e?.message,
            errorCode: networkError?.code,
          },
        });
      }
    }

    if (redirect) {
      navigate(Urls.getTeamOnboardingUrl(orgName), {
        trigger: true,
        replace: true,
      });
    }
  };

  return {
    startFreeTrial,
    addingTrial,
    isAdmin,
    hasCredit,
    hasActivePromoCredits,
    isTrialActive: trialProperties?.isActive,
    isExpired: trialProperties?.isExpired,
    isEligible:
      isAdmin &&
      !hasNewCredit &&
      !isDisabled &&
      isConfirmed &&
      !isEnterpriseMember &&
      !isPremium &&
      !hasStandardPromoCredits,
    loading,
    isStandard,
    isPremium,
    isEnterpriseMember,
    daysLeft: trialProperties?.daysLeft || 0,
    trialSource: trialProperties?.trialSource,
    startDate: trialProperties?.startDate,
    days: trialProperties?.days,
    endDate: trialProperties?.expiresAt ?? null,
    totalFreeTrialCredits,
  };
};

// TODO: rethink the original useFreeTrialEligibilityRules implementation
// to account for checks in multiple teams
export const GTM_LAUNCH_DATE = new Date('8/24/2021');
